import type { AssignmentPointsNoteDoc } from '../types/assignmentPointsNote';
import {
  loadAssignmentPointsNoteCache,
  saveAssignmentPointsNoteCache,
} from './assignmentPointsNoteStorage';
import {
  isIsoNewer,
  isMainPresentationBrowserTab,
  postBroadcastChannel,
  subscribeBroadcastChannel,
  waitForCacheHit,
} from './noteDocTabBridgeCore';

const CHANNEL_NAME = 'cp-assignment-points-note-v1';
const REQUEST_CHANNEL_NAME = 'cp-assignment-points-note-request-v1';

export type AssignmentPointsNoteBridgeMessage = {
  v: 1;
  type: 'doc';
  workspaceId: string;
  gradeId: string;
  doc: AssignmentPointsNoteDoc;
  savedAt: number;
};

type AssignmentPointsNoteRequestMessage = {
  type: 'request';
  workspaceId: string;
  gradeId: string;
};

/** 메인 탭(또는 최근 로드/저장 탭) 메모리 — miss 워밍용 */
let livePublished: AssignmentPointsNoteDoc | null = null;

function isDocMessage(data: unknown): data is AssignmentPointsNoteBridgeMessage {
  if (!data || typeof data !== 'object') return false;
  const o = data as AssignmentPointsNoteBridgeMessage;
  return (
    o.v === 1 &&
    o.type === 'doc' &&
    typeof o.workspaceId === 'string' &&
    typeof o.gradeId === 'string' &&
    !!o.doc &&
    typeof o.doc === 'object'
  );
}

export function setLiveAssignmentPointsNoteDoc(doc: AssignmentPointsNoteDoc | null): void {
  livePublished = doc;
}

/** LS 저장 + BroadcastChannel 배포 (다른 탭 React state 동기화) */
export function publishAssignmentPointsNoteDoc(doc: AssignmentPointsNoteDoc): void {
  if (!doc.workspaceId || !doc.gradeId) return;
  livePublished = doc;
  /* 프레젠 탭 즉시 hit를 위해 needsReload 중이어도 강제 저장 */
  saveAssignmentPointsNoteCache(doc, { force: true });
  const msg: AssignmentPointsNoteBridgeMessage = {
    v: 1,
    type: 'doc',
    workspaceId: doc.workspaceId,
    gradeId: doc.gradeId,
    doc,
    savedAt: Date.now(),
  };
  postBroadcastChannel(CHANNEL_NAME, msg);
}

export function requestAssignmentPointsNoteFromMainTab(input: {
  workspaceId: string;
  gradeId: string;
}): void {
  postBroadcastChannel(REQUEST_CHANNEL_NAME, {
    type: 'request',
    workspaceId: input.workspaceId.trim(),
    gradeId: input.gradeId.trim(),
  } satisfies AssignmentPointsNoteRequestMessage);
}

export function subscribeAssignmentPointsNoteDoc(
  onDoc: (doc: AssignmentPointsNoteDoc) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const unsubBc = subscribeBroadcastChannel(CHANNEL_NAME, (msg) => {
    onDoc(msg.doc);
  }, isDocMessage);

  const onStorage = (e: StorageEvent) => {
    if (!e.key?.startsWith('cp.assignmentPointsNote.v1.') || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue) as AssignmentPointsNoteDoc;
      if (parsed?.workspaceId && parsed?.gradeId) onDoc(parsed);
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    unsubBc();
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * LS → (miss) 메인 탭 워밍 대기 → 그래도 없으면 null.
 * 메인 프레젠 호출측은 null이어도 DB로 폴백하지 않는다.
 */
export async function resolveAssignmentPointsNoteFromCacheOrMainTab(
  workspaceId: string,
  gradeId: string
): Promise<AssignmentPointsNoteDoc | null> {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws || !gid) return null;

  const hit = () => loadAssignmentPointsNoteCache(ws, gid);
  const first = hit();
  if (first) return first;

  await waitForCacheHit({
    tryHit: () => !!hit(),
    onRequest: () => requestAssignmentPointsNoteFromMainTab({ workspaceId: ws, gradeId: gid }),
  });
  return hit();
}

/** 메인 앱 탭: 프레젠/다른 탭 request에 LS·live·필요 시 DB로 응답 */
export function installMainTabAssignmentPointsNoteResponder(): () => void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(REQUEST_CHANNEL_NAME);
    ch.onmessage = (ev: MessageEvent<AssignmentPointsNoteRequestMessage>) => {
      const msg = ev.data;
      if (!msg || msg.type !== 'request') return;
      const workspaceId = (msg.workspaceId ?? '').trim();
      const gradeId = (msg.gradeId ?? '').trim();
      if (!workspaceId || !gradeId) return;
      void warmAssignmentPointsNoteForOpen({ workspaceId, gradeId });
    };
  } catch {
    ch = null;
  }

  return () => {
    try {
      ch?.close();
    } catch {
      /* ignore */
    }
  };
}

export async function warmAssignmentPointsNoteForOpen(input: {
  workspaceId: string;
  gradeId: string;
}): Promise<{ ok: boolean; reason: string }> {
  const workspaceId = input.workspaceId.trim();
  const gradeId = input.gradeId.trim();
  if (!workspaceId || !gradeId) return { ok: false, reason: 'workspace/grade 없음' };

  if (
    livePublished &&
    livePublished.workspaceId === workspaceId &&
    livePublished.gradeId === gradeId
  ) {
    publishAssignmentPointsNoteDoc(livePublished);
    return { ok: true, reason: 'live' };
  }

  const cached = loadAssignmentPointsNoteCache(workspaceId, gradeId);
  if (cached) {
    publishAssignmentPointsNoteDoc(cached);
    return { ok: true, reason: 'localStorage' };
  }

  /* 프레젠 탭에서는 DB 워밍 금지 — 메인 탭 responder만 REST */
  if (isMainPresentationBrowserTab()) {
    return { ok: false, reason: 'presentation-no-db' };
  }

  try {
    const { getAssignmentPointsNote } = await import('../services/assignmentPointsNoteService');
    const doc = await getAssignmentPointsNote(workspaceId, gradeId);
    publishAssignmentPointsNoteDoc(doc);
    return { ok: true, reason: 'db' };
  } catch (err) {
    console.warn('[assignmentPointsNoteTabBridge] warm 실패', err);
    return { ok: false, reason: 'db-error' };
  }
}

/** 원격 doc을 적용할지 — 로컬 dirty이거나 로컬이 더 최신이면 false */
export function shouldApplyRemoteAssignmentPointsNote(options: {
  remote: AssignmentPointsNoteDoc;
  local: AssignmentPointsNoteDoc | null;
  isDirty: boolean;
}): boolean {
  if (options.isDirty) return false;
  const { remote, local } = options;
  if (!local) return true;
  if (local.workspaceId !== remote.workspaceId || local.gradeId !== remote.gradeId) {
    return false;
  }
  if (local.updatedAt === remote.updatedAt) return true;
  return isIsoNewer(remote.updatedAt, local.updatedAt);
}
